<template>
  <section class="tw:flex tw:max-w-[620px] tw:flex-col tw:gap-7" data-testid="reg-step-profile">
    <RegistrationStepHeading :title="$t('registration.profile.title')" :lede="$t('registration.profile.lede')" />

    <div class="tw:flex tw:flex-col tw:gap-[18px]">
      <div class="tw:grid tw:grid-cols-2 tw:gap-4">
        <LeadInput
          v-model="firstName"
          type="text"
          autocomplete="given-name"
          :label="$t('registration.profile.first_name')"
          :errors="fieldErrors.firstName"
          required
          data-testid="reg-first-name" />

        <LeadInput
          v-model="lastName"
          type="text"
          autocomplete="family-name"
          :label="$t('registration.profile.last_name')"
          :errors="fieldErrors.lastName"
          required
          data-testid="reg-last-name" />
      </div>

      <div class="tw:grid tw:grid-cols-2 tw:gap-4">
        <LeadInput
          v-model="jobTitle"
          type="text"
          autocomplete="organization-title"
          :label="$t('registration.profile.job_title')"
          data-testid="reg-job-title">
          <template #iconStart><IconBriefcase :size="16" /></template>
        </LeadInput>

        <!-- same value as the header selector; the platform persists the language onto the account on every request -->
        <div class="tw:flex tw:flex-col tw:gap-1.5">
          <label class="tw:text-14 tw:font-medium tw:text-gray-700" for="reg-language">
            {{ $t('registration.profile.language') }}
          </label>
          <LanguageSelect id="reg-language" class="tw:w-full" data-testid="reg-profile-language" />
        </div>
      </div>
    </div>

    <!-- mobile confirmation is required before the step can be submitted -->
    <PhoneVerification
      testid-prefix="reg-mobile"
      data-testid="reg-mobile-card"
      :title="$t('registration.profile.mobile_title')"
      :masked-number="displayNumber"
      :verified="mobileVerified"
      :initial-number="store.draft.account.mobile"
      :error="mobileError"
      :send="sendCode"
      :verify="verifyCode"
      @verified="onVerified"
      @number-changed="onNumberChanged" />
  </section>
</template>

<script setup lang="ts">
/** Step 3 - the name and the mobile confirmation are both required to continue. Language is the header's selector, so there is no field for it */
import { IconBriefcase } from '@tabler/icons-vue';
import { computed, onMounted, reactive, ref, watch } from 'vue';

import { registrationClient } from '@/api/registration';
import RegistrationStepHeading from '@/components/registration/subcomponents/RegistrationStepHeading.vue';
import LeadInput from '@/components/lead-design/LeadInput.vue';
import LanguageSelect from '@/components/generic/LanguageSelect.vue';
import PhoneVerification from '@/components/generic/PhoneVerification.vue';
import { useRegistrationStepSync } from '@/composables/useRegistrationStepSync';
import translator from '@/locale/translator';
import { useRegistrationStore } from '@/stores/registration';
import { useUserStore } from '@/stores/user';
import type { PhoneSendRequest, PhoneSendResult, PhoneVerifyRequest } from '@/types/types';

/* ----------------------------------- STATE ---------------------------------- */
const allowNext = defineModel<boolean>('allowNext', { required: true });

const store = useRegistrationStore();
const userStore = useUserStore();

const mobileError = ref('');
const fieldErrors = reactive<{ firstName: string; lastName: string }>({
  firstName: '',
  lastName: '',
});

/* --------------------------------- COMPUTED --------------------------------- */
// bound to the draft so a reload recovers them
const firstName = computed({
  get: () => store.draft.profile.firstName,
  set: (value: string) => {
    store.draft.profile.firstName = value;
  },
});

const lastName = computed({
  get: () => store.draft.profile.lastName,
  set: (value: string) => {
    store.draft.profile.lastName = value;
  },
});

const jobTitle = computed({
  get: () => store.draft.profile.jobTitle,
  set: (value: string) => {
    store.draft.profile.jobTitle = value;
  },
});

const mobileVerified = computed(() => store.mobileVerified);

/** masked number to show; the backend owns the mask, with the step 1 mask as fallback for the mock flow */
const displayNumber = computed(() => {
  return userStore.currentUser?.phone_number ?? store.verification.maskedMobile ?? '';
});

const isFormValid = computed(
  () => firstName.value.trim() !== '' && lastName.value.trim() !== '' && mobileVerified.value
);

/* --------------------------------- METHODS ---------------------------------- */
function clearErrors() {
  fieldErrors.firstName = '';
  fieldErrors.lastName = '';
  mobileError.value = '';
}

async function sendCode({ phone, channel }: PhoneSendRequest): Promise<PhoneSendResult> {
  const response =
    channel === 'call'
      ? await registrationClient.requestVoiceCode()
      : await registrationClient.resendSmsCode(phone ? { mobile: phone } : {});

  return { resendAfterSeconds: response.resendAfterSeconds, maskedNumber: response.maskedMobile };
}

async function verifyCode({ code }: PhoneVerifyRequest) {
  const response = await registrationClient.verifyMobile({ code });

  return response.mobileVerified;
}

function onVerified() {
  mobileError.value = '';
  // the durable record is on the backend; this flag keeps the UI in step and survives the language reload
  store.mobileVerified = true;
  userStore.updatePhone({ phone_verified_at: new Date().toISOString() });
}

function onNumberChanged() {
  // a changed number was never confirmed
  store.mobileVerified = false;
  userStore.updatePhone({ phone_verified_at: null });
}

async function submit(): Promise<boolean> {
  clearErrors();

  if (!firstName.value.trim()) fieldErrors.firstName = translator.get('registration.profile.name_required');
  if (!lastName.value.trim()) fieldErrors.lastName = translator.get('registration.profile.name_required');
  if (!mobileVerified.value) {
    mobileError.value = translator.get('registration.profile.mobile_required');
    return false;
  }
  if (!firstName.value.trim() || !lastName.value.trim()) return false;

  // language is not sent: the platform persists it on every request
  await registrationClient.submitProfile({
    firstName: firstName.value.trim(),
    lastName: lastName.value.trim(),
    jobTitle: jobTitle.value.trim(),
  });

  return true;
}

// pre-fills the draft from the backend on every entry; `isFormValid` already watches the draft
useRegistrationStepSync('profile');

/* -------------------------------- LIFECYCLE --------------------------------- */
onMounted(() => {
  store.availableSteps.profile.onContinue = submit;
  allowNext.value = isFormValid.value;
  // the code is not requested on load: an SMS is the user's to trigger
});

/* --------------------------------- WATCHERS --------------------------------- */
watch(isFormValid, (valid) => {
  allowNext.value = valid;
});
</script>
